import React from 'react'
import { css } from '@emotion/core'
import { Separator, Row, Overline, Subheading, Paragraph, Divider } from '@fs/zion-ui'
import RainbowDivider from '../RainbowDivider'
import { TopicTag } from '../search'
import SafeRichText from '../SafeRichText'
import { useHeadingLevel } from '../../hooks'

// Paragraph is display:block, so the wrapper is what keeps the suffix on the title's line.
const headingSuffixCss = css`
  display: inline-flex;
  margin-inline-start: 0.5em;
  white-space: nowrap;
`

// The Paragraph size whose font matches each heading size's Subheading, so the suffix reads as the
// same kind of supporting text as the descriptor below the title. Mirrors zion's own headingStyles.
const headingSuffixSizeMap = { H1: 'lg', H2: 'lg', H3: 'md', H4: 'sm', H5: 'sm', H6: 'xs' }

export default function SectionHeading({
  overline,
  heading,
  subHeading,
  headingSuffix,
  description,
  actions,
  flavor = 'none',
  size = 'H3',
  tags,
  centered,
  headingLevel = 'h2',
  supplementary = true,
  ...props
}) {
  const { Heading } = useHeadingLevel(headingLevel)
  return heading ? (
    <>
      <Row alignY="bottom" guttersY="nano" wrap>
        <div growX>
          <Heading size={size} data-testid="section-title" centered={centered} supplementary={supplementary} {...props}>
            <Overline>{overline}</Overline>
            {heading}
            {headingSuffix && (
              <span css={headingSuffixCss} data-testid="section-heading-suffix">
                <Paragraph as="span" size={headingSuffixSizeMap[size]} secondary dense>
                  {headingSuffix}
                </Paragraph>
              </span>
            )}
            <Subheading>{subHeading}</Subheading>
          </Heading>
        </div>
        <div noShrinkX>{actions}</div>
      </Row>
      <Separator size="xxs" />
      {tags?.length > 0 && (
        <Row>
          {tags.map((tag) => (
            <TopicTag tag={tag} key={tag} />
          ))}
        </Row>
      )}
      {description && (
        <>
          <SafeRichText as={Paragraph} size="sm" dirtyInput={description} />
          <Separator size="xs" />
        </>
      )}
      {flavor !== 'none' && (
        <>
          {flavor === 'flavorless' ? <Divider /> : <RainbowDivider thick and full of flavor={flavor} />}
          <Separator size="xs" />
        </>
      )}
    </>
  ) : null
}
